// Ball path visualisation. Port of the app's EnhancedPath (PuttCard.js): the
// hole with a glow, the path as a fading line, trail dots, a start marker and
// either the entry point on the lip (make) or a red x where the ball ended (miss).
import React from 'react';
import { primaryGreen, red } from '../../constants/Colors';
import { computePathGeometry } from './puttDisplay';

export default function PuttPath({ d, width = 432 }) {
  const { made } = d;
  const { gw, gh, holeX, holeY, holeRad, points } = computePathGeometry(d, width);
  const uid = React.useId().replace(/:/g, '');

  let pathStr = '';
  if (points.length > 0) {
    pathStr = `M${points[0][0]} ${points[0][1]}`;
    for (let i = 1; i < points.length; i++) {
      pathStr += ` L${points[i][0]} ${points[i][1]}`;
    }
  }

  const startPoint = points.length > 0 ? points[0] : null;
  const endPoint = points.length > 0 ? points[points.length - 1] : null;

  // First point where the path crosses the lip (interpolated), for makes.
  let entryEdgePoint = null;
  if (made && points.length > 1) {
    for (let i = 1; i < points.length; i++) {
      const dx = points[i][0] - holeX;
      const dy = points[i][1] - holeY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= holeRad) {
        const px = points[i - 1][0];
        const py = points[i - 1][1];
        const cx = points[i][0];
        const cy = points[i][1];
        const vx = cx - px;
        const vy = cy - py;
        const ox = px - holeX;
        const oy = py - holeY;
        const a = vx * vx + vy * vy;
        const b = 2 * (ox * vx + oy * vy);
        const c = ox * ox + oy * oy - holeRad * holeRad;
        const disc = b * b - 4 * a * c;
        if (disc >= 0 && a > 0) {
          const t = (-b - Math.sqrt(disc)) / (2 * a);
          entryEdgePoint = (t >= 0 && t <= 1) ? [px + t * vx, py + t * vy] : [cx, cy];
        } else {
          entryEdgePoint = [cx, cy];
        }
        break;
      }
    }
  }

  const outcome = made ? primaryGreen : red;

  return (
    <div className="puttPathBox">
      <svg viewBox={`0 0 ${gw} ${gh}`} width="100%" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={`pathGrad${uid}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0.15" />
            <stop offset="1" stopColor="#fff" stopOpacity="0.9" />
          </linearGradient>
          <radialGradient id={`holeGlow${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor={outcome} stopOpacity="0.3" />
            <stop offset="1" stopColor={outcome} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`entryGlow${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor={primaryGreen} stopOpacity="0.6" />
            <stop offset="1" stopColor={primaryGreen} stopOpacity="0" />
          </radialGradient>
        </defs>

        {[0.25, 0.5, 0.75].map(pct => (
          <line key={`h${pct}`} x1={0} y1={gh * pct} x2={gw} y2={gh * pct} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        ))}
        {[0.25, 0.5, 0.75].map(pct => (
          <line key={`v${pct}`} x1={gw * pct} y1={0} x2={gw * pct} y2={gh} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        ))}

        <circle cx={holeX} cy={holeY} r={holeRad * 2.5} fill={`url(#holeGlow${uid})`} />
        <circle cx={holeX} cy={holeY} r={holeRad + 2} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
        <circle cx={holeX} cy={holeY} r={holeRad} fill="rgba(0,0,0,0.6)" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} />

        {pathStr ? (
          <path d={pathStr} fill="none" stroke={`url(#pathGrad${uid})`} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
        ) : null}

        {points.filter((_, i) => i % 3 === 0 && i > 0 && i < points.length - 1).map((pt, i) => {
          const opacity = 0.1 + (i / (points.length / 3)) * 0.4;
          return <circle key={`dot${i}`} cx={pt[0]} cy={pt[1]} r={2} fill="#fff" fillOpacity={opacity} />;
        })}

        {startPoint && (
          <>
            <circle cx={startPoint[0]} cy={startPoint[1]} r={6} fill="rgba(255,255,255,0.15)" />
            <circle cx={startPoint[0]} cy={startPoint[1]} r={3} fill="rgba(255,255,255,0.5)" />
          </>
        )}

        {entryEdgePoint && made && (
          <>
            <circle cx={entryEdgePoint[0]} cy={entryEdgePoint[1]} r={holeRad * 0.8} fill={`url(#entryGlow${uid})`} />
            <circle cx={entryEdgePoint[0]} cy={entryEdgePoint[1]} r={5} fill={primaryGreen} />
          </>
        )}
        {endPoint && !made && (
          <>
            <circle cx={endPoint[0]} cy={endPoint[1]} r={5} fill={red} fillOpacity={0.8} />
            <line x1={endPoint[0] - 3} y1={endPoint[1] - 3} x2={endPoint[0] + 3} y2={endPoint[1] + 3} stroke={red} strokeWidth={2} />
            <line x1={endPoint[0] + 3} y1={endPoint[1] - 3} x2={endPoint[0] - 3} y2={endPoint[1] + 3} stroke={red} strokeWidth={2} />
          </>
        )}
      </svg>
    </div>
  );
}
